import React, { useRef } from 'react';
import { FieldWrapper } from '@progress/kendo-react-form';
import ImageFileUploader from '../inputs/ImageFileUploader';
import { Label, Error, Hint } from '@progress/kendo-react-labels';
// eslint-disable-next-line no-unused-vars
import BaseFormControl from './BaseFormControl';

/**
 * An image file uploader form field.
 *
 * @typedef {React.ComponentProps<BaseFormControl>} BaseFieldRenderProps
 * @typedef {React.ComponentPropsWithoutRef<ImageFileUploader>} ImageFileUploaderProps
 *
 * @param {BaseFieldRenderProps & ImageFileUploaderProps} fieldRenderProps
 * @returns {React.ReactElement} Image file uploader form field.
 */
const FormImageFileUploader = (fieldRenderProps) => {
  const {
    validationMessage,
    touched,
    label,
    optional,
    id,
    name,
    valid,
    disabled,
    hint,
    value,
    labelStyle,
    wrapperClassName,
    ...others
  } = fieldRenderProps;

  const editorId = id ?? name;

  const editorRef = useRef(null);
  const showValidationMessage = touched && validationMessage;
  const showHint = !showValidationMessage && hint;
  const hintId = showHint ? `${editorId}_hint` : '';
  const errorId = showValidationMessage ? `${editorId}_error` : '';
  const labelId = label ? `${editorId}_label` : '';

  return (
    <FieldWrapper className={wrapperClassName}>
      <Label
        id={labelId}
        editorRef={editorRef}
        editorId={editorId}
        editorValid={valid}
        editorDisabled={disabled}
        optional={optional}
        style={labelStyle}
      >
        {label}
      </Label>
      <ImageFileUploader
        ref={editorRef}
        ariaLabelledBy={labelId}
        ariaDescribedBy={`${hintId} ${errorId}`}
        valid={valid}
        id={editorId}
        name={name}
        disabled={disabled}
        value={value}
        // HACK
        readOnly={wrapperClassName === 'read-only-field'}
        {...others}
      />
      {showHint && <Hint id={hintId}>{hint}</Hint>}
      {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
    </FieldWrapper>
  );
};

export default FormImageFileUploader;
